/**
 * Owns creator publishing HTTP contracts. Source text and canonical entity IDs
 * cross this boundary; persistence rows, provider fields, and secrets do not.
 */
import { z } from "zod";

export const canonicalIdSchema = z
  .string()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    "Invalid UUID"
  );
const nonEmptyTextSchema = z.string().trim().min(1).max(200_000);
const titleSchema = z.string().trim().min(1).max(240);
const timestampSchema = z.iso.datetime({ offset: true });

export const workTermTreatmentSchema = z.enum([
  "preserve",
  "preferred_translation",
  "annotate",
  "context_only"
]);

export const workContextTermSchema = z
  .object({
    sourceTerm: z.string().trim().min(1).max(80),
    treatment: workTermTreatmentSchema,
    preferredRendering: z.string().trim().min(1).max(120).optional(),
    explanation: z.string().trim().min(1).max(280).optional(),
    spoiler: z.boolean()
  })
  .strict()
  .superRefine((term, context) => {
    if (
      term.treatment === "preferred_translation" &&
      !term.preferredRendering
    ) {
      context.addIssue({
        code: "custom",
        path: ["preferredRendering"],
        message: "Preferred translation requires a rendering"
      });
    }
    if (term.treatment === "annotate" && !term.explanation) {
      context.addIssue({
        code: "custom",
        path: ["explanation"],
        message: "Annotation treatment requires an explanation"
      });
    }
  });

export const workContextSchema = z
  .object({
    setting: z.string().trim().max(1_000),
    terms: z.array(workContextTermSchema).max(20)
  })
  .strict()
  .refine(
    ({ terms }) =>
      new Set(
        terms.map(({ sourceTerm }) =>
          sourceTerm.normalize("NFKC").trim().toLocaleLowerCase("en")
        )
      ).size === terms.length,
    { message: "Work Context terms must be unique", path: ["terms"] }
  );

function uniqueIds(minimum = 0) {
  return z
    .array(canonicalIdSchema)
    .min(minimum)
    .max(100)
    .refine((ids) => new Set(ids).size === ids.length, {
      message: "Canonical entity IDs must be unique"
    });
}

export const canonicalEntitySelectionSchema = z
  .object({
    fandomIds: uniqueIds(1),
    characterIds: uniqueIds(),
    shipIds: uniqueIds(),
    tagIds: uniqueIds()
  })
  .strict();

const finalSourceFields = {
  title: titleSchema,
  sourceLanguage: z.literal("zh-CN"),
  sourceBody: nonEmptyTextSchema,
  fandomIds: uniqueIds(1),
  characterIds: uniqueIds(),
  shipIds: uniqueIds(),
  tagIds: uniqueIds(),
  workContext: workContextSchema.optional()
} as const;

export const contentCreateRequestSchema = z
  .object({
    kind: z.literal("work"),
    ...finalSourceFields
  })
  .strict();

export const contentUpdateRequestSchema = z
  .object({
    expectedUpdatedAt: timestampSchema,
    title: titleSchema.optional(),
    sourceLanguage: z.literal("zh-CN").optional(),
    sourceBody: nonEmptyTextSchema.optional(),
    fandomIds: uniqueIds(1).optional(),
    characterIds: uniqueIds().optional(),
    shipIds: uniqueIds().optional(),
    tagIds: uniqueIds().optional(),
    workContext: workContextSchema.optional()
  })
  .strict()
  .refine(
    (request) =>
      Object.keys(request).some((field) => field !== "expectedUpdatedAt"),
    { message: "At least one draft field must be updated" }
  );

export const contentPublishRequestSchema = z
  .object({
    idempotencyKey: z.string().trim().min(1).max(120),
    ...finalSourceFields
  })
  .strict();

const contentStateSchema = z
  .object({
    id: canonicalIdSchema,
    kind: z.literal("work"),
    status: z.enum(["draft", "published"]),
    updatedAt: timestampSchema
  })
  .strict();

export const contentDraftResponseSchema = z
  .object({
    content: contentStateSchema,
    draft: z
      .object({
        title: titleSchema,
        sourceLanguage: z.literal("zh-CN"),
        sourceBody: nonEmptyTextSchema,
        workContext: workContextSchema.optional()
      })
      .strict(),
    canonicalEntities: canonicalEntitySelectionSchema
  })
  .strict();

export const contentPublishResponseSchema = z
  .object({
    content: contentStateSchema.extend({
      status: z.literal("published")
    }),
    sourceVersion: z
      .object({
        id: canonicalIdSchema,
        versionNumber: z.number().int().positive(),
        language: z.literal("zh-CN"),
        title: titleSchema,
        workContext: workContextSchema.optional()
      })
      .strict(),
    canonicalEntities: canonicalEntitySelectionSchema,
    translationStatus: z.literal("pending")
  })
  .strict();

export type ContentCreateRequest = z.infer<typeof contentCreateRequestSchema>;
export type ContentUpdateRequest = z.infer<typeof contentUpdateRequestSchema>;
export type ContentPublishRequest = z.infer<typeof contentPublishRequestSchema>;
export type ContentDraftResponse = z.infer<typeof contentDraftResponseSchema>;
export type ContentPublishResponse = z.infer<
  typeof contentPublishResponseSchema
>;
